import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllLots } from '../../services/Lot/lotService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreateLotModal from '../Lot/CreateLotModal';

const LotInput = ({ value, onChange, label, isInvalid, userRoles, createdNew }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState(value || { id: null, description: '', lot: null });
  const [searchText, setSearchText] = useState(value?.description || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [lots, setLots] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedLot(value || { id: null, description: '', lot: null });
    setSearchText(value?.description || '');
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchLots = useCallback(async () => {
    if (!shouldFetch) return;
    try {
      const data = await getAllLots(filterParams, page - 1, pageSize);
      setLots(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar lotes:', error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchLots();
    }
  }, [page, pageSize, filterParams, fetchLots, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filter = { description: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectLot = (lot) => {
    setSelectedLot(lot);
    setSearchText(lot.description);
    setIsReadOnly(true);
    onChange({ description: lot.description, id: lot.id, lot: lot });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedLot({ id: null, description: '', lot: null });
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
  };

  const handleCloseModal = () => {
    if (!selectedLot.id) {
      setSearchText('');
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecione um lote"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Lote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', display: createdNew ? 'block' : 'none' }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>

          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Fazenda</th>
                <th>Cliente</th>
                <th>Produto</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((lot) => (
                <tr key={lot.id}>
                  <td>{lot.description}</td>
                  <td>{lot.creditValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                  <td>{lot.farm ? lot.farm.name : '-'}</td>
                  <td>{lot.person ? lot.person.name : '-'}</td>
                  <td>{lot.product ? lot.product.description : '-'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectLot(lot)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={lots.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <CreateLotModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedLot(created);
          onChange(created);
        }}
      />
    </>
  );
};

export default LotInput;